//@flow

import React from 'react';
import type { Node } from 'react';

import Cookies from '../../lib/cookies';

type Props = {
  /**
   * It fires when the mouse cursor moves close to (or passes) the top of the viewport.
   * You can define how far the mouse has to be before it fires.
   *  The higher value, the more sensitive, and the more quickly the event will fire. Defaults to 20.
   */
  sensitivity?: number,

  /**
   * You could instead configure it to wait x milliseconds to delay the callback.
   * If the user's mouse re-enters the body before delay ms have passed, the callback won't be fired.
   * Defaults to 300.
   */

  delay?: number,

  /**
   *  a function that will run once it has been triggered.
   */
  callback: () => void,

  /**
   *  name of the cookie.
   */
  cookieName: string,

  /**
   *  value to be stored inside the cookie.
   */
  cookieValue?: number,

  /**
   *  expiry time of the set cookie
   */
  cookieExpiryTime?: number,

  /**
   *  children of the class
   */
  children: Node,
};

/**
 * A class track when the user leaves the document.
 * Additionally a cookie should be defined by giving the name via args to
 * disable the callback untill the cookie is expired.
 */

export default class UserExitsDocument extends React.Component<Props> {
  static defaultProps = {
    callback: () => {},
    delay: 300,
    sensitivity: 20,
    cookieName: null,
    cookieExpiryTime: 1,
    cookieValue: 1,
  };

  delayTimer = null;

  componentDidMount() {
    this.addEventListeners();
  }

  addEventListeners = () => {
    const doc = document.documentElement;

    doc.addEventListener('mouseleave', this.handleMouseLeave);
    doc.addEventListener('mouseenter', this.handleMouseEnter);
  };

  handleMouseEnter = () => {
    if (this.delayTimer) {
      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    }
  };

  handleMouseLeave = e => {
    const { sensitivity, delay } = this.props;
    const currentMousePosition = e.clientY;
    const mouseOutSideOfDocument = currentMousePosition <= sensitivity;

    if (mouseOutSideOfDocument) this.delayTimer = setTimeout(() => this.fire(), delay);
  };

  fire = () => {
    const { callback, cookieExpiryTime, cookieName, cookieValue } = this.props;

    if (this.disabled()) return;

    if (!this.isCookieDefined()) {
      throw new Error(
        'useWhenUserExitsDocument hook requires a cookie name. Expected string but got null'
      );
    }

    Cookies.prototype.setCookie(cookieName, cookieValue, cookieExpiryTime);
    callback();
  };

  isCookieDefined = () => Boolean(this.props.cookieName);

  disabled = () => Boolean(Cookies.prototype.getCookie(this.props.cookieName));

  componentWillUnmount() {
    this.cleanup();
  }

  cleanup = () => {
    const doc = document.documentElement;

    doc.removeEventListener('mouseleave', this.handleMouseLeave);
    doc.removeEventListener('mouseenter', this.handleMouseEnter);

    clearTimeout(this.delayTimer);
  };

  render() {
    const { children } = this.props;

    return children ? children : null;
  }
}



// WEBPACK FOOTER //
// ./src/components/UserExitsDocument/UserExitsDocument.jsx